import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { classLevelWord, formatClassLevelLabel } from './classLevelLabel';

/** Shape returned by GET /api/school-settings/{school}/term-export */
export interface TermExportPayload {
  school: string;
  term: number;
  academic_year: string;
  term_label?: string;
  term_start_date?: string | null;
  term_end_date?: string | null;
  exported_at: string;
  totals?: {
    total_students: number;
    students_by_form: Record<string, number>;
    total_grades_recorded: number;
    total_fee_expected: number;
    total_fee_paid: number;
    total_fee_outstanding: number;
    total_payments_recorded: number;
  };
  students?: Array<{
    student_name: string | null;
    student_id: string | null;
    form: number | null;
    status?: string | null;
    boarding_status?: string | null;
    total_fee: string | number | null;
    opening_balance?: string | number | null;
    amount_paid: string | number | null;
    balance: string | number | null;
    carry_over_credit: string | number | null;
    charges?: Array<{ title: string | null; amount: string | number | null }>;
  }>;
  grades?: Array<{
    student_name: string | null;
    student_id: string | null;
    form: number | null;
    subject: string | null;
    marks: number | null;
    grade: string | null;
    type: string;
    published_to_students?: boolean;
  }>;
  fee_payments?: Array<{
    student_name: string | null;
    student_id: string | null;
    receipt_number: string | null;
    amount: string | number | null;
    payment_date: string | null;
  }>;
}

const money = (v: string | number | null | undefined): string => {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  return isNaN(n) ? '—' : n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const text = (v: string | number | null | undefined): string =>
  v === null || v === undefined || v === '' ? '—' : String(v);

const HEADER_COLOR: [number, number, number] = [5, 150, 105];
const PAGE_MARGIN = 40;

/** Build the end-of-term data export PDF (summary, fee balances, grades, payments). */
export function buildTermExportPdf(data: TermExportPayload, schoolDisplayName: string): jsPDF {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const termLabel = data.term_label || `Term ${data.term} ${data.academic_year}`;

  // ── Title block ──
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text(schoolDisplayName, pageWidth / 2, 46, { align: 'center' });
  doc.setFontSize(12);
  doc.text(`${termLabel} — End of Term Data Export`, pageWidth / 2, 64, { align: 'center' });
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  const exportedAt = new Date(data.exported_at).toLocaleString();
  const dates =
    data.term_start_date || data.term_end_date
      ? `Term dates: ${text(data.term_start_date)} to ${text(data.term_end_date)}   |   `
      : '';
  doc.text(`${dates}Exported: ${exportedAt}`, pageWidth / 2, 80, { align: 'center' });

  /** Draw a bold section heading, adding a page break when too close to the bottom. */
  const sectionTitle = (label: string, y: number): number => {
    if (y > pageHeight - 100) {
      doc.addPage();
      y = 50;
    }
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text(label, PAGE_MARGIN, y);
    doc.setFont('helvetica', 'normal');
    return y + 8;
  };

  const lastY = (): number => (doc as any).lastAutoTable.finalY as number;

  // ── Summary ──
  const totals = data.totals;
  const byForm = totals?.students_by_form
    ? Object.entries(totals.students_by_form)
        .sort(([a], [b]) => Number(a) - Number(b))
        .map(([form, count]) => `${formatClassLevelLabel(data.school, Number(form))}: ${count}`)
        .join('   ')
    : '—';

  let y = sectionTitle('Term Summary', 104);
  autoTable(doc, {
    startY: y,
    body: [
      ['Total students in this export', String(totals?.total_students ?? '—')],
      ['Students by class level', byForm],
      ['Grades recorded this term', String(totals?.total_grades_recorded ?? '—')],
      ['Total fees expected', money(totals?.total_fee_expected)],
      ['Total fees paid', money(totals?.total_fee_paid)],
      ['Total fees outstanding (balances)', money(totals?.total_fee_outstanding)],
      ['Payments recorded this term', String(totals?.total_payments_recorded ?? '—')],
    ],
    theme: 'grid',
    styles: { fontSize: 9, cellPadding: 4 },
    columnStyles: { 0: { fontStyle: 'bold', cellWidth: 220 } },
    margin: { left: PAGE_MARGIN, right: PAGE_MARGIN },
  });

  // ── Students & fee balances ──
  const students = data.students ?? [];
  y = sectionTitle('Students & Fee Balances', lastY() + 28);
  autoTable(doc, {
    startY: y,
    head: [['#', 'Student', 'ID', classLevelWord(data.school), 'Status', 'Boarding', 'Brought forward', 'Total Fee', 'Paid', 'Balance']],
    body: students.map((s, i) => [
      String(i + 1),
      text(s.student_name),
      text(s.student_id),
      text(s.form),
      text(s.status),
      text(s.boarding_status || 'unset'),
      money(s.opening_balance),
      money(s.total_fee),
      money(s.amount_paid),
      money(s.balance),
    ]),
    theme: 'striped',
    headStyles: { fillColor: HEADER_COLOR },
    styles: { fontSize: 8, cellPadding: 3 },
    margin: { left: PAGE_MARGIN, right: PAGE_MARGIN },
  });

  const chargeRows = students.flatMap((s) =>
    (s.charges ?? []).map((c) => [
      text(s.student_name),
      text(s.student_id),
      text(c.title),
      money(c.amount),
    ])
  );
  y = sectionTitle('Specific Charges Applied This Term', lastY() + 28);
  autoTable(doc, {
    startY: y,
    head: [['Student', 'ID', 'Charge', 'Amount']],
    body: chargeRows.length > 0 ? chargeRows : [['—', '—', 'No specific charges', '—']],
    theme: 'striped',
    headStyles: { fillColor: HEADER_COLOR },
    styles: { fontSize: 8, cellPadding: 3 },
    margin: { left: PAGE_MARGIN, right: PAGE_MARGIN },
  });

  // ── Grades ──
  const grades = data.grades ?? [];
  y = sectionTitle('Grades Recorded During the Term', lastY() + 28);
  autoTable(doc, {
    startY: y,
    head: [['Student', 'ID', classLevelWord(data.school), 'Subject', 'Marks', 'Grade', 'Type', 'Published']],
    body: grades.map((g) => [
      text(g.student_name),
      text(g.student_id),
      text(g.form),
      text(g.subject),
      text(g.marks),
      text(g.grade),
      text(g.type),
      g.published_to_students ? 'Yes' : 'No',
    ]),
    theme: 'striped',
    headStyles: { fillColor: HEADER_COLOR },
    styles: { fontSize: 8, cellPadding: 3 },
    margin: { left: PAGE_MARGIN, right: PAGE_MARGIN },
  });

  // ── Fee payments ──
  const payments = data.fee_payments ?? [];
  y = sectionTitle('Fee Payments Recorded During the Term', lastY() + 28);
  autoTable(doc, {
    startY: y,
    head: [['Student', 'ID', 'Receipt No.', 'Amount', 'Payment Date']],
    body: payments.map((p) => [
      text(p.student_name),
      text(p.student_id),
      text(p.receipt_number),
      money(p.amount),
      text(p.payment_date),
    ]),
    theme: 'striped',
    headStyles: { fillColor: HEADER_COLOR },
    styles: { fontSize: 8, cellPadding: 3 },
    margin: { left: PAGE_MARGIN, right: PAGE_MARGIN },
  });

  // ── Page numbers ──
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text(
      `${schoolDisplayName} — ${termLabel} export — page ${i} of ${pageCount}`,
      pageWidth / 2,
      pageHeight - 16,
      { align: 'center' }
    );
  }

  return doc;
}
